import React, { useMemo, useState, useRef, Component, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeftIcon,
  ThermometerIcon,
  DropletsIcon,
  ZapIcon,
  ActivityIcon,
  SettingsIcon,
  LightbulbIcon,
  ToggleLeftIcon,
  GaugeIcon,
  WindIcon,
  CloudIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  LockIcon,
  KeyIcon,
  XIcon,
  PencilIcon,
  StarIcon,
  PlusIcon,
  CheckIcon,
  PersonStandingIcon,
  RadarIcon,
  HeartPulseIcon,
  ShieldCheckIcon,
  Link2Icon
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, LineChart, Line
} from 'recharts';

import { useTelemetry } from '../hooks/useTelemetry';
import { type TimeRange as HookTimeRange } from '../hooks/useHistoricalTelemetry';
import { thingsboardService } from '../services/thingsboard';

import {
  type SensorType,
  getSensorIcon,
  getSensorColorObject,
  SerIcon,
  SemIcon,
} from '../utils/sensorUtils';

export type TimeRange = HookTimeRange;
export type Mode = 'normal' | 'always' | 'co2';

// SEL Mode types
export type SELMode = 'occupancy' | 'normal' | 'ai';
export const SEL_MODES: {
  id: SELMode;
  label: string;
}[] = [
{
  id: 'occupancy',
  label: '재실'
},
{
  id: 'normal',
  label: '일반'
},
{
  id: 'ai',
  label: 'AI모드'
}];

export interface Sensor {
  id: string;
  type: SensorType;
  label: string;
  roomId: string;
  active: boolean;
  primary_appliance?: string;
  appliance_names?: string;
  [key: string]: any;
}
export interface SensorDetailSidebarProps {
  sensor: Sensor;
  onBack: () => void;
  onClose: () => void;
  onUpdateSensor?: (updatedSensor: Sensor) => void;
}

export const MODES: {
  id: Mode;
  label: string;
  value: number;
}[] = [
{
  id: 'normal',
  label: '재실',
  value: 1
},
{
  id: 'always',
  label: '상시',
  value: 2
},
{
  id: 'co2',
  label: 'CO2',
  value: 3
}];

export const TIME_RANGE_LABELS: Record<TimeRange, string> = {
  realtime: '실시간',
  daily: '일간',
  weekly: '주간',
  monthly: '월간'
};



// Reusable time range tab bar
export function TimeRangeTabs({
  value,
  onChange,
  disabledRanges = []
}: {
  value: TimeRange;
  onChange: (t: TimeRange) => void;
  disabledRanges?: TimeRange[];
}) {
  return (
    <div className="flex bg-white/5 rounded-lg p-0.5">
      {(['realtime', 'daily', 'weekly', 'monthly'] as TimeRange[]).map((t) => {
      const isDisabled = disabledRanges.includes(t);
      return (
      <button
        key={t}
        onClick={() => { if (!isDisabled) onChange(t); }}
        disabled={isDisabled}
        className={`
            px-2 py-1 rounded-md text-[0.7rem] uppercase font-bold transition-all
            ${value === t ? 'bg-white/10 text-white shadow-sm' : isDisabled ? 'text-slate-700 cursor-not-allowed' : 'text-slate-500 hover:text-slate-300'}
          `}>

          {t.slice(0, 1).toUpperCase()}
        </button>
      )})}
    </div>);

}
// Reusable chart section
export function ChartSection({
  icon: Icon,
  iconColor = 'text-slate-400',
  title,
  data,
  timeRange,
  onTimeRangeChange,
  disabledRanges = [],
  chartType = 'area',
  color,
  gradientId,
  stats,
  className = '',
  unit = ''
}: {icon?: any;iconColor?: string;title: string;data: {name: string;value: number;}[];timeRange: TimeRange;onTimeRangeChange: (t: TimeRange) => void;disabledRanges?: TimeRange[];chartType?: 'area' | 'bar' | 'line';color?: string;gradientId?: string;stats?: {label: string;value: string;color?: string;}[];className?: string;unit?: string;}) {
  const [localChartType, setLocalChartType] = useState<'area' | 'bar' | 'line'>(
    chartType
  );
  return (
    <motion.div
      initial={{
        opacity: 0,
        y: 10
      }}
      animate={{
        opacity: 1,
        y: 0
      }}
      className={`glass-panel rounded-xl p-3 flex flex-col ${className}`}>

      <div className="flex items-center justify-between mb-2 flex-shrink-0">
        <div className="flex items-center gap-2">
          {Icon && <Icon className={`w-3.5 h-3.5 ${iconColor}`} />}
          <span className="text-[0.9rem] font-bold text-slate-300">
            {title}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {/* Chart type toggle */}
          <div className="flex bg-white/5 rounded-lg p-0.5 mr-2">
            <button
              onClick={() => setLocalChartType('bar')}
              className={`p-1 rounded-md transition-all ${localChartType === 'bar' ? 'bg-white/10 text-white shadow-sm' : 'text-slate-500 hover:text-slate-300'}`}
              title="Bar Chart">
              
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1"
                strokeLinecap="round"
                strokeLinejoin="round">
                
                <rect x="3" y="12" width="4" height="9" rx="1" />
                <rect x="10" y="6" width="4" height="15" rx="1" />
                <rect x="17" y="3" width="4" height="18" rx="1" />
              </svg>
            </button>
            <button
              onClick={() => setLocalChartType('line')}
              className={`p-1 rounded-md transition-all ${localChartType === 'line' ? 'bg-white/10 text-white shadow-sm' : 'text-slate-500 hover:text-slate-300'}`}
              title="Line Chart">
              
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round">
                
                <path d="M3 16 L9 11 L15 14 L21 6" />
              </svg>
            </button>
            <button
              onClick={() => setLocalChartType('area')}
              className={`p-1 rounded-md transition-all ${localChartType === 'area' ? 'bg-white/10 text-white shadow-sm' : 'text-slate-500 hover:text-slate-300'}`}
              title="Area Chart">
              
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round">
                
                <path
                  d="M3 20 L3 8 L9 13 L15 6 L21 12 L21 20 Z"
                  fill="currentColor"
                  opacity="0.2" />
                
                <path d="M3 8 L9 13 L15 6 L21 12" />
              </svg>
            </button>
          </div>
        <TimeRangeTabs value={timeRange} onChange={onTimeRangeChange} disabledRanges={disabledRanges} />
      </div>
      </div>

      <div className="flex-1 min-h-0 w-full">
        <ResponsiveContainer width="100%" height="100%">
          {localChartType === 'bar' ?
          <BarChart data={data}>
              <XAxis
                dataKey="name"
                tickLine={false}
                axisLine={false}
                tick={{ fill: '#64748b', fontSize:'0.7rem' }}
                minTickGap={20}
                dy={10}
              />
              <Tooltip
              cursor={{
                fill: 'rgba(255,255,255,0.05)'
              }}
              contentStyle={{
                backgroundColor: '#0a0f1a',
                borderColor: '#334155',
                fontSize: '0.7rem'
              }}
              itemStyle={{
                color
              }}           
              formatter={(value: number) => [`${Number(value).toFixed(1)} ${unit}`.trim()]} />

              <Bar
              dataKey="value"
              fill={color}
              radius={[2, 2, 0, 0]}
              opacity={0.8}
              minPointSize={2} />

            </BarChart> :
          localChartType === 'line' ?
          <LineChart data={data}>
              <XAxis
                dataKey="name"
                tickLine={false}
                axisLine={false}
                tick={{ fill: '#64748b', fontSize: '0.7rem' }}
                minTickGap={20}
                dy={10}
              />
              <Tooltip
              contentStyle={{
                backgroundColor: '#0a0f1a',
                borderColor: '#334155',
                fontSize: '0.7rem'
              }}
              itemStyle={{
                color
              }}
              formatter={(value: number) => [`${Number(value).toFixed(1)} ${unit}`.trim()]} />

              <Line
              type="monotone"
              dataKey="value"
              stroke={color}
              strokeWidth={2}
              dot={false} />

            </LineChart> :

          <AreaChart data={data}>
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={color} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="name"
                tickLine={false}
                axisLine={false}
                tick={{ fill: '#64748b', fontSize: '0.7rem' }}
                minTickGap={20}
                dy={10}
              />
              <YAxis hide />
              <Tooltip
              contentStyle={{
                backgroundColor: '#0a0f1a',
                borderColor: '#334155',
                fontSize: '0.7rem'
              }}
              itemStyle={{
                color
              }}
              formatter={(value: number) => [`${Number(value).toFixed(1)} ${unit}`.trim()]} />

              <Area
              type="monotone"
              dataKey="value"
              stroke={color}
              fillOpacity={1}
              fill={`url(#${gradientId})`} />

            </AreaChart>
          }
        </ResponsiveContainer>
      </div>

      {stats &&
      <div className="flex justify-between mt-1.5 text-xs flex-shrink-0">
          {stats.map((s, i) =>
        <div key={i} className="text-center">
              <div className="text-slate-500 text-[0.7rem] uppercase">
                {s.label}
              </div>
              <div className={`font-roboto ${s.color || 'text-white'}`}>
                {s.value}
              </div>
            </div>
        )}
        </div>
      }
    </motion.div>);

}
// ─── Password Toast Component ───
export function PasswordToast({
  type,
  onClose,
  onVerify,
  onChangeComplete,
  storedPassword

}: {type: 'verify' | 'change';onClose: () => void;onVerify?: (success: boolean) => void;onChangeComplete?: (newPw: string) => void;storedPassword: string;}) {
  const [step, setStep] = useState<'verify' | 'newPw' | 'confirm'>(
    type === 'verify' ? 'verify' : 'verify'
  );
  const [digits, setDigits] = useState(['', '', '', '']);
  const [newDigits, setNewDigits] = useState(['', '', '', '']);
  const [error, setError] = useState('');
  const inputRefs = [
  useRef<HTMLInputElement>(null),
  useRef<HTMLInputElement>(null),
  useRef<HTMLInputElement>(null),
  useRef<HTMLInputElement>(null)];

  const newInputRefs = [
  useRef<HTMLInputElement>(null),
  useRef<HTMLInputElement>(null),
  useRef<HTMLInputElement>(null),
  useRef<HTMLInputElement>(null)];

  const handleDigitChange = (
  index: number,
  value: string,
  refs: React.RefObject<HTMLInputElement>[],
  setter: React.Dispatch<React.SetStateAction<string[]>>) =>
  {
    if (value.length > 1) value = value.slice(-1);
    if (value && !/^\d$/.test(value)) return;
    setter((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
    setError('');
    if (value && index < 3) {
      refs[index + 1]?.current?.focus();
    }
  };
  const handleKeyDown = (
  index: number,
  e: React.KeyboardEvent,
  refs: React.RefObject<HTMLInputElement>[],
  currentDigits: string[],
  setter: React.Dispatch<React.SetStateAction<string[]>>) =>
  {
    if (e.key === 'Backspace' && !currentDigits[index] && index > 0) {
      refs[index - 1]?.current?.focus();
    }
  };
  const renderInputs = (
  currentDigits: string[],
  refs: React.RefObject<HTMLInputElement>[],
  setter: React.Dispatch<React.SetStateAction<string[]>>) =>

  <div className="flex gap-2 justify-center my-4">
      {currentDigits.map((d, i) =>
    <input
      key={i}
      ref={refs[i]}
      type="password"
      inputMode="numeric"
      maxLength={1}
      value={d}
      onChange={(e) => handleDigitChange(i, e.target.value, refs, setter)}
      onKeyDown={(e) => handleKeyDown(i, e, refs, currentDigits, setter)}
      className="w-10 h-12 rounded-lg bg-white/5 border border-white/20 text-center text-lg font-roboto text-white focus:border-cyan-400 focus:outline-none focus:ring-1 focus:ring-cyan-400/50 transition-all"
      autoFocus={i === 0} />

    )}
    </div>;

  const handleVerify = () => {
    const entered = digits.join('');
    if (entered.length < 4) {
      setError('4자리를 모두 입력해주세요');
      return;
    }
    if (entered !== storedPassword) {
      setError('비밀번호가 일치하지 않습니다');
      setDigits(['', '', '', '']);
      inputRefs[0]?.current?.focus();
      return;
    }
    if (type === 'verify') {
      onVerify?.(true);
    } else {
      setStep('newPw');
      setError('');
    }
  };
  const handleSetNew = () => {
    const newPw = newDigits.join('');
    if (newPw.length < 4) {
      setError('4자리를 모두 입력해주세요');
      return;
    }
    onChangeComplete?.(newPw);
  };
  const getTitle = () => {
    if (step === 'verify')
    return type === 'verify' ? '비밀번호 확인' : '기존 비밀번호 확인';
    return '새 비밀번호 설정';
  };
  const getSubtitle = () => {
    if (step === 'verify') return '기존 비밀번호 4자리를 입력하세요';
    return '새로운 비밀번호 4자리를 입력하세요';
  };
  return (
    <motion.div
      initial={{
        opacity: 0
      }}
      animate={{
        opacity: 1
      }}
      exit={{
        opacity: 0
      }}
      className="fixed inset-0 z-[200] flex items-center justify-center"
      onClick={onClose}>

      <div className="absolute inset-0 bg-black/85 backdrop-blur-2xl backdrop-saturate-0 backdrop-brightness-50" />
      <motion.div
        initial={{
          opacity: 0,
          scale: 0.9,
          y: 20
        }}
        animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }}
        exit={{
          opacity: 0,
          scale: 0.9,
          y: 20
        }}
        transition={{
          type: 'spring',
          stiffness: 400,
          damping: 30
        }}
        onClick={(e) => e.stopPropagation()}
        className="relative z-10 w-[90%] max-w-sm">

        <div className="glass-panel rounded-2xl p-6 border-l-4 border-l-cyan-400 shadow-2xl">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-lg bg-cyan-500/15 text-cyan-400">
              <KeyIcon className="w-5 h-5" />
            </div>
            <div>
              <div className="text-base font-bold text-white">{getTitle()}</div>
              <div className="text-[0.65rem] text-slate-500">
                {getSubtitle()}
              </div>
            </div>
          </div>

          {step === 'verify' ?
          renderInputs(digits, inputRefs, setDigits) :
          renderInputs(newDigits, newInputRefs, setNewDigits)}

          <AnimatePresence>
            {error &&
            <motion.div
              initial={{
                opacity: 0,
                y: -5
              }}
              animate={{
                opacity: 1,
                y: 0
              }}
              exit={{
                opacity: 0
              }}
              className="text-red-400 text-xs text-center mb-3 font-medium">

                {error}
              </motion.div>
            }
          </AnimatePresence>

          <div className="flex gap-3 justify-end">
            <button
              onClick={onClose}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white/5 border border-white/20 text-slate-300 text-sm font-medium hover:bg-white/10 transition-all active:scale-95">

              <XIcon className="w-3.5 h-3.5" />
              취소
            </button>
            <button
              onClick={step === 'verify' ? handleVerify : handleSetNew}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-cyan-500/20 border border-cyan-500/30 text-cyan-400 text-sm font-medium hover:bg-cyan-500/30 transition-all active:scale-95">

              <LockIcon className="w-3.5 h-3.5" />
              {step === 'verify' ? '확인' : '설정'}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>);

}

// ─── Preset appliance names ───
export const PRESET_NAMES = ['냉장고', 'TV', '세탁기', '건조기', '식기세척기', '에어드레서', '큐커', '인덕션', '로봇청소기', '김치냉장고', '공기청정기'];
// ─── Name Edit Popup ───
export function NameEditPopup({
  sensor,
  currentNames,
  primaryName,
  onSave,
  onClose

}: {sensor: Sensor;currentNames: string[];primaryName: string;onSave: (names: string[], primary: string) => void;onClose: () => void;}) {
  const [selectedNames, setSelectedNames] = useState<string[]>(currentNames);
  const [primary, setPrimary] = useState<string>(primaryName);
  const [customInput, setCustomInput] = useState('');
  const [customNames, setCustomNames] = useState<string[]>(
    currentNames.filter((n) => !PRESET_NAMES.includes(n) && n !== sensor.label)
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const Icon = getSensorIcon(sensor.type);
  const sensorColors = getSensorColorObject(sensor.type);
  const allNames = [...PRESET_NAMES, ...customNames];
  const toggleName = (name: string) => {
    setSelectedNames((prev) => {
      const next = prev.includes(name) ?
      prev.filter((n) => n !== name) :
      [...prev, name];
      // If removing the primary, reset primary
      if (!next.includes(primary)) {
        setPrimary(next[0] || '');
      }
      return next;
    });
  };
  const addCustomName = () => {
    const trimmed = customInput.trim();
    if (!trimmed || allNames.includes(trimmed)) return;
    setCustomNames((prev) => [...prev, trimmed]);
    setSelectedNames((prev) => [...prev, trimmed]);
    if (!primary) setPrimary(trimmed);
    setCustomInput('');
    inputRef.current?.focus();
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addCustomName();
    }
  };
  const removeCustomName = (name: string) => {
    setCustomNames((prev) => prev.filter((n) => n !== name));
    setSelectedNames((prev) => prev.filter((n) => n !== name));
    if (primary === name) {
      const remaining = selectedNames.filter((n) => n !== name);
      setPrimary(remaining[0] || '');
    }
  };
  const handleSave = () => {
    if (selectedNames.length === 0) {
      onSave([], '');
      return;
    }
    onSave(selectedNames, primary || selectedNames[0]);
  };
  return (
    <motion.div
      initial={{
        opacity: 0
      }}
      animate={{
        opacity: 1
      }}
      exit={{
        opacity: 0
      }}
      className="fixed inset-0 z-[200] flex items-center justify-center"
      onClick={onClose}>

      <div className="absolute inset-0 bg-black/85 backdrop-blur-2xl backdrop-saturate-0 backdrop-brightness-50" />
      <motion.div
        initial={{
          opacity: 0,
          scale: 0.9,
          y: 20
        }}
        animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }}
        exit={{
          opacity: 0,
          scale: 0.9,
          y: 20
        }}
        transition={{
          type: 'spring',
          stiffness: 400,
          damping: 30
        }}
        onClick={(e) => e.stopPropagation()}
        className="relative z-10 w-[92%] max-w-md">

        <div className="glass-panel rounded-2xl p-5 shadow-2xl border border-white/20">
          {/* Header */}
          <div className="flex items-center gap-3 mb-4">
            <div className={`p-2.5 rounded-xl ${sensorColors.bg}`}>
              <Icon className={`w-5 h-5 ${sensorColors.icon}`} />
            </div>
            <div className="flex-1">
              <div className="text-base font-bold text-white">
                센서 이름 설정
              </div>
              <div className="text-[0.8rem] text-slate-500 font-roboto">
                {sensor.label} · {sensor.type}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-white/10 transition-colors text-slate-400 hover:text-white">

              <XIcon className="w-4 h-4" />
            </button>
          </div>

          {/* Preset Names */}
          <div className="mb-4">
            <div className="text-[0.8rem] text-slate-500 uppercase tracking-wider font-bold mb-2 px-0.5">
              프리셋 선택
            </div>
            <div className="flex flex-wrap gap-2">
              {PRESET_NAMES.map((name) => {
                const isSelected = selectedNames.includes(name);
                const isPrimary = primary === name;
                return (
                  <button
                    key={name}
                    onClick={() => toggleName(name)}
                    className={`
                      relative flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all active:scale-95
                      ${isSelected ? 'bg-cyan-500/20 border border-cyan-500/40 text-cyan-300' : 'bg-white/5 border border-white/20 text-slate-400 hover:text-slate-200 hover:bg-white/10'}
                    `}>

                    {isSelected && <CheckIcon className="w-3 h-3" />}
                    {name}
                    {isPrimary && isSelected &&
                    <StarIcon className="w-3 h-3 text-amber-400 fill-amber-400" />
                    }
                  </button>);

              })}
            </div>
          </div>

          {/* Custom Names */}
          {customNames.length > 0 &&
          <div className="mb-4">
              <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-2 px-0.5">
                사용자 정의
              </div>
              <div className="flex flex-wrap gap-2">
                {customNames.map((name) => {
                const isSelected = selectedNames.includes(name);
                const isPrimary = primary === name;
                return (
                  <div key={name} className="flex items-center gap-0">
                      <button
                      onClick={() => toggleName(name)}
                      className={`
                          flex items-center gap-1.5 pl-3 pr-1.5 py-1.5 rounded-l-lg text-xs font-medium transition-all active:scale-95
                          ${isSelected ? 'bg-emerald-500/20 border border-r-0 border-emerald-500/40 text-emerald-300' : 'bg-white/5 border border-r-0 border-white/20 text-slate-400 hover:text-slate-200'}
                        `}>

                        {isSelected && <CheckIcon className="w-3 h-3" />}
                        {name}
                        {isPrimary && isSelected &&
                      <StarIcon className="w-3 h-3 text-amber-400 fill-amber-400" />
                      }
                      </button>
                      <button
                      onClick={() => removeCustomName(name)}
                      className={`
                          p-1.5 rounded-r-lg text-xs transition-all active:scale-95
                          ${isSelected ? 'bg-emerald-500/20 border border-l-0 border-emerald-500/40 text-red-400 hover:text-red-300' : 'bg-white/5 border border-l-0 border-white/20 text-slate-600 hover:text-red-400'}
                        `}>

                        <XIcon className="w-3 h-3" />
                      </button>
                    </div>);

              })}
              </div>
            </div>
          }

          {/* Custom Input */}
          <div className="mb-4">
            <div className="text-[0.8rem] text-slate-500 uppercase tracking-wider font-bold mb-2 px-0.5">
              직접 입력
            </div>
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={customInput}
                onChange={(e) => setCustomInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="이름 입력..."
                className="flex-1 px-3 py-2 rounded-lg bg-white/5 border border-white/20 text-sm text-white placeholder-slate-600 focus:border-cyan-400 focus:outline-none focus:ring-1 focus:ring-cyan-400/30 transition-all" />

              <button
                onClick={addCustomName}
                disabled={!customInput.trim()}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-cyan-500/20 border border-cyan-500/30 text-cyan-400 text-sm font-medium hover:bg-cyan-500/30 transition-all active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed">

                <PlusIcon className="w-3.5 h-3.5" />
                추가
              </button>
            </div>
          </div>

          {/* Selected Summary & Primary Selection */}
          {selectedNames.length > 0 &&
          <div className="mb-4">
              <div className="text-[0.8rem] text-slate-500 uppercase tracking-wider font-bold mb-2 px-0.5 flex items-center gap-1.5">
                <StarIcon className="w-3 h-3 text-amber-400" />
                대표 이름 지정
              </div>
              <div className="flex flex-wrap gap-1.5">
                {selectedNames.map((name) => {
                const isPrimary = primary === name;
                return (
                  <button
                    key={name}
                    onClick={() => setPrimary(name)}
                    className={`
                        flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all active:scale-95
                        ${isPrimary ? 'bg-amber-500/20 border border-amber-500/40 text-amber-300 shadow-[0_0_10px_rgba(245,158,11,0.15)]' : 'bg-white/5 border border-white/20 text-slate-400 hover:text-white hover:bg-white/10'}
                      `}>

                      <StarIcon
                      className={`w-3 h-3 ${isPrimary ? 'text-amber-400 fill-amber-400' : 'text-slate-600'}`} />

                      {name}
                    </button>);

              })}
              </div>
            </div>
          }

          {/* Actions */}
          <div className="flex gap-3 justify-end pt-2 border-t border-white/20">
            <button
              onClick={onClose}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white/5 border border-white/20 text-slate-300 text-sm font-medium hover:bg-white/10 transition-all active:scale-95">

              취소
            </button>
            <button
              onClick={handleSave}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all active:scale-95 bg-cyan-500/20 border border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/30`}>

              <CheckIcon className="w-3.5 h-3.5" />
              저장
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>);

}


// ─── Reusable Toggle Switch ───
export function ToggleSwitch({
  isOn,
  onToggle,
}: {
  isOn: boolean
  onToggle: () => void
}) {
  return (
    <button
      onClick={onToggle}
      className={`relative w-16 h-8 rounded-full transition-colors duration-300 focus:outline-none flex-shrink-0 ${isOn ? 'bg-emerald-500' : 'bg-slate-700'}`}
    >
      <div
        className={`absolute top-1 left-1.5 w-6 h-6 rounded-full bg-white shadow-sm transition-transform duration-300 ${isOn ? 'translate-x-7' : 'translate-x-0'}`}
      />
    </button>
  )
}



// ─── Shared Header ───
export function SensorHeader({
  sensor,
  subtitle,
  onBack,
  onClose,
  rightSlot,
  extraAction,
  onUpdateSensor
}: {
  sensor: Sensor;
  subtitle: string;
  onBack: () => void;
  onClose: () => void;
  rightSlot?: React.ReactNode;
  extraAction?: React.ReactNode;
  onUpdateSensor?: (sensor: Sensor) => void;
}) {
  const [showNameEdit, setShowNameEdit] = useState(false);
  const [selectedNames, setSelectedNames] = useState<string[]>([]);
  const [primaryName, setPrimaryName] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Fetch saved names from ThingsBoard attributes when sensor changes
  useEffect(() => {
    const fetchNames = async () => {
      try {
        const attributes = await thingsboardService.getAttributes('DEVICE', sensor.id, 'appliance_names,primary_appliance', 'SERVER_SCOPE');
        
        let fetchedNames: string[] = [];
        let fetchedPrimary = '';

        if (attributes && attributes.length > 0) {
          const namesAttr = attributes.find((a: any) => a.key === 'appliance_names');
          const primaryAttr = attributes.find((a: any) => a.key === 'primary_appliance');
          
          if (namesAttr?.value && typeof namesAttr.value === 'string' && namesAttr.value.trim() !== '') {
            try {
              const parsed = JSON.parse(namesAttr.value);
              fetchedNames = Array.isArray(parsed) ? parsed : [parsed];
            } catch (e) {
              fetchedNames = namesAttr.value.split(',');
            }
          }
          if (primaryAttr?.value) {
            fetchedPrimary = primaryAttr.value;
          }
        }
        
        setSelectedNames(fetchedNames);
        setPrimaryName(fetchedPrimary);
      } catch (err) {
        console.error('Failed to fetch appliance names', err);
      }
    };
    
    if (sensor.type === 'SEO' || sensor.type === 'SER' || sensor.type === 'SEM') {
       fetchNames();
    }
  }, [sensor.id, sensor.type]);

  const getIcon = () => {
    switch (sensor.type) {
      case 'SEO':
        return ZapIcon;
      case 'SEM':
        return SemIcon;
      case 'SER':
        return SerIcon;
      case 'SEL':
        return LightbulbIcon;
      case 'SES':
        return ToggleLeftIcon;
      case 'RADAR':
        return RadarIcon;
      default:
        return ActivityIcon;
    }
  };
  const getIconColor = () => {
    switch (sensor.type) {
      case 'SEO':
      case 'SER':
        return 'text-violet-400 bg-violet-500/15 border-violet-500/30';
      case 'SEM':
        return 'text-blue-400 bg-blue-500/15 border-blue-500/30';
      case 'SEL':
        return 'text-amber-400 bg-amber-500/15 border-amber-500/30';
      case 'SES':
        return 'text-cyan-400 bg-cyan-500/15 border-cyan-500/30';
      case 'RADAR':
        return 'text-rose-400 bg-rose-500/15 border-rose-500/30';
      default:
        return 'text-white bg-white/15 border-white/30';
    }
  };
  const Icon = getIcon();
  const displayName = primaryName || sensor.label;
  const handleSaveNames = async (names: string[], primary: string) => {
    setIsSaving(true);
    try {
      await thingsboardService.saveEntityAttributes('DEVICE', sensor.id, 'SERVER_SCOPE', {
        appliance_names: names.length > 0 ? JSON.stringify(names) : '',
        primary_appliance: primary
      });
      setSelectedNames(names);
      setPrimaryName(primary);
      setShowNameEdit(false);
      onUpdateSensor?.({
        ...sensor,
        appliance_names: names.length > 0 ? JSON.stringify(names) : '',
        primary_appliance: primary
      });
    } catch (err) {
      console.error('Failed to save appliance names', err);
      setSelectedNames(names);
      setPrimaryName(primary);
      setShowNameEdit(false);
      onUpdateSensor?.({
        ...sensor,
        appliance_names: names.length > 0 ? JSON.stringify(names) : '',
        primary_appliance: primary
      });
    } finally {
      setIsSaving(false);
    }
  };
  return (
    <>
      <div className="flex items-center gap-2.5 mb-1 flex-shrink-0">
        <button
          onClick={onBack}
          className="p-1.5 rounded-full hover:bg-white/10 transition-colors text-slate-400 hover:text-white">
          <ArrowLeftIcon className="w-5 h-5" />
        </button>
        <div className={`p-1.5 rounded-lg border ${getIconColor()}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[0.6rem] font-roboto uppercase tracking-widest text-slate-500">
            {subtitle}
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-base font-bold text-white truncate">
              {displayName}
            </span>
            {selectedNames.length > 1 && (
              <span className="text-[9px] text-slate-500 bg-white/5 px-1.5 py-0.5 rounded-full font-roboto">
                +{selectedNames.length - 1}
              </span>
            )}
            {sensor.active ? (
              <motion.span
                className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-emerald-400"
                animate={{ opacity: [1, 0.4, 1] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              />
            ) : (
              <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-slate-600" />
            )}

            <button
              onClick={() => setShowNameEdit(true)}
              className="p-1 rounded-md hover:bg-white/10 transition-colors text-slate-500 hover:text-cyan-400 flex-shrink-0"
              title="이름 수정"
            >
              <PencilIcon className="w-3 h-3" />
            </button>

            {extraAction}
          </div>
        </div>
        {/* Right slot: sensor-specific controls */}
        {rightSlot && (
          <div className="flex-shrink-0 self-center mr-2 mt-1">{rightSlot}</div>
        )}
      </div>

      <AnimatePresence>
        {showNameEdit && (
          <NameEditPopup
            sensor={sensor}
            currentNames={selectedNames}
            primaryName={primaryName}
            onSave={handleSaveNames}
            onClose={() => setShowNameEdit(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
}

// ─── Switch Connect Popup for SEL ───
export function SwitchConnectPopup({
  sensor,
  onSave,
  onClose
}: {
  sensor: Sensor;
  onSave: (sesDevice: { id: string; name: string } | null) => void;
  onClose: () => void;
}) {
  const [loading, setLoading] = useState(true);
  const [zoneName, setZoneName] = useState<string>('');
  const [sesDevices, setSesDevices] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedSesId, setSelectedSesId] = useState<string>('');
  const [currentSesId, setCurrentSesId] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        // 1. Fetch current operate relation from this SEL
        const rels = await thingsboardService.getRelations('DEVICE', sensor.id);
        const operateRel = rels.find((r: any) => r.type === 'operate' && r.to.entityType === 'DEVICE');
        if (operateRel) {
          setCurrentSesId(operateRel.to.id);
          setSelectedSesId(operateRel.to.id);
        }

        // 2. Fetch Zone and candidate SES devices
        const zoneInfo = await thingsboardService.getZoneSesDevices(sensor.id);
        setZoneName(zoneInfo.zoneName);
        setSesDevices(zoneInfo.sesDevices);
      } catch (err) {
        console.error('Failed to load SES relation info:', err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [sensor.id]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // If previously linked to an SES and selection changed/cleared -> delete old relation
      if (currentSesId && currentSesId !== selectedSesId) {
        await thingsboardService.deleteRelation(sensor.id, 'DEVICE', currentSesId, 'DEVICE', 'operate');
      }
      // If a new SES is selected -> create new relation
      if (selectedSesId && selectedSesId !== currentSesId) {
        await thingsboardService.createRelation(sensor.id, 'DEVICE', selectedSesId, 'DEVICE', 'operate');
      }

      const selectedDev = sesDevices.find(d => d.id === selectedSesId) || null;
      onSave(selectedDev);
      onClose();
    } catch (err) {
      console.error('Failed to save SES relation:', err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="w-full max-w-sm rounded-2xl border border-white/20 bg-slate-900/95 p-5 shadow-2xl backdrop-blur-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between pb-3 mb-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-xl bg-cyan-500/15 border border-cyan-500/30 text-cyan-400">
              <Link2Icon className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-white">스위치 연결</h3>
              <p className="text-xs text-slate-400">{sensor.label} ({zoneName || '존'})</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>

        {loading ? (
          <div className="py-8 flex flex-col items-center justify-center gap-2 text-slate-400">
            <div className="w-6 h-6 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
            <span className="text-xs">스위치 목록 조회 중...</span>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-xs text-slate-400 font-medium">
              연동할 <span className="text-cyan-300 font-bold">{zoneName || '해당 구역'}</span>의 스마트 스위치(SES)를 선택하세요.
            </div>

            <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
              {/* Option: Unlink / None */}
              <label
                className={`flex items-center justify-between p-3 rounded-xl border transition-all cursor-pointer ${
                  selectedSesId === ''
                    ? 'bg-rose-500/15 border-rose-500/40 text-white'
                    : 'bg-white/5 border-white/10 text-slate-400 hover:bg-white/10 hover:text-slate-200'
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <input
                    type="radio"
                    name="ses-select"
                    checked={selectedSesId === ''}
                    onChange={() => setSelectedSesId('')}
                    className="accent-rose-400"
                  />
                  <span className="text-xs font-semibold">연결 안 함 (해제)</span>
                </div>
                {currentSesId === '' && (
                  <span className="text-[10px] bg-slate-800 text-slate-400 px-2 py-0.5 rounded-full">현재 상태</span>
                )}
              </label>

              {/* List SES devices */}
              {sesDevices.length === 0 ? (
                <div className="py-4 text-center text-xs text-slate-500 bg-white/5 rounded-xl border border-white/5">
                  해당 공간에 등록된 SES 스위치가 없습니다.
                </div>
              ) : (
                sesDevices.map((ses) => {
                  const isSelected = selectedSesId === ses.id;
                  const isCurrent = currentSesId === ses.id;
                  return (
                    <label
                      key={ses.id}
                      className={`flex items-center justify-between p-3 rounded-xl border transition-all cursor-pointer ${
                        isSelected
                          ? 'bg-cyan-500/20 border-cyan-400/50 text-white shadow-lg shadow-cyan-950/40'
                          : 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      <div className="flex items-center gap-2.5">
                        <input
                          type="radio"
                          name="ses-select"
                          checked={isSelected}
                          onChange={() => setSelectedSesId(ses.id)}
                          className="accent-cyan-400"
                        />
                        <div className="flex flex-col">
                          <span className="text-xs font-bold text-white">{ses.name}</span>
                          <span className="text-[10px] text-slate-400">스마트 스위치</span>
                        </div>
                      </div>
                      {isCurrent && (
                        <span className="text-[10px] bg-cyan-500/30 text-cyan-300 border border-cyan-400/40 px-2 py-0.5 rounded-full font-bold">
                          연결됨
                        </span>
                      )}
                    </label>
                  );
                })
              )}
            </div>

            <div className="flex gap-2 justify-end pt-3 mt-3 border-t border-white/10">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-xs font-semibold text-slate-300 hover:bg-white/10 transition-all"
              >
                취소
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 text-xs font-bold shadow-lg shadow-cyan-500/25 transition-all disabled:opacity-50"
              >
                <CheckIcon className="w-3.5 h-3.5" />
                {isSaving ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}


